import { memo, useMemo } from 'react';
import type { AIReceptionistEvent } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-path/v1/api.pb';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { PhoneEventsOrg } from '@wetags/phone-events';
import { Trans, useTranslation } from '@frontend/i18n';
import { AI_EVENT_CONFIG, getAiEventCallPathDescription } from '@frontend/phone-ui-widgets';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { DotRow, EventRowList, formatTime } from '../../utils/get-event-accordion-body';

export const AIReceptionistEventComponent = memo(
  ({ enteredAt, exitedAt, actionType, reason, aiReceptionistEventTypes }: AIReceptionistEvent) => {
    const { t } = useTranslation('calls');
    const [isAirEventsUiEnabled] = PhoneEventsOrg.useWetag('fe-air-events-ui');
    const { createdAtTime, endedAtTime } = useMemo(
      () => ({
        createdAtTime: formatTime(enteredAt),
        endedAtTime: formatTime(exitedAt),
      }),
      [enteredAt, exitedAt]
    );

    const showTransferedCallMessage = actionType === AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED;
    const reasonColor = actionType === AIOutcome.AI_OUTCOME_FATAL ? 'error' : 'light';
    const callEndedByAi = actionType === AIOutcome.AI_OUTCOME_END_CALL;

    if (!isAirEventsUiEnabled) {
      return (
        <>
          <div>
            <DotRow
              message={
                <Trans t={t}>
                  <Text weight='bold' size='medium' as='span'>
                    AI Receptionist
                  </Text>{' '}
                  <Text as='span' size='medium'>
                    answers the call
                  </Text>
                </Trans>
              }
              time={createdAtTime}
              hideDot={!showTransferedCallMessage}
            />
            {showTransferedCallMessage && (
              <DotRow
                message={
                  <Trans t={t}>
                    <Text weight='bold' size='medium' as='span'>
                      AI Receptionist
                    </Text>{' '}
                    <Text as='span' size='medium'>
                      transfers call
                    </Text>
                  </Trans>
                }
                time={endedAtTime}
                hideDot
              />
            )}
          </div>

          {!callEndedByAi && (
            <Text color={reasonColor} textAlign='left' size='small' css={{ paddingTop: theme.spacing(2) }}>
              {reason}
            </Text>
          )}
        </>
      );
    }

    const events = [
      {
        message: (
          <Trans t={t}>
            <Text weight='bold' size='medium' as='span'>
              AI Receptionist
            </Text>{' '}
            <Text as='span' size='medium'>
              answers the call
            </Text>
          </Trans>
        ),
        time: createdAtTime,
      },
      ...(aiReceptionistEventTypes?.map((event) => ({
        iconEvent: AI_EVENT_CONFIG[event.eventType]?.event,
        message:
          getAiEventCallPathDescription(event.eventType, t) ??
          AI_EVENT_CONFIG[event.eventType]?.text ??
          event.eventType,
        time: event.eventTimestamp ? formatTime(event.eventTimestamp) : '',
      })) ?? []),
      ...(showTransferedCallMessage
        ? [
            {
              message: (
                <Trans t={t}>
                  <Text weight='bold' size='medium' as='span'>
                    AI Receptionist
                  </Text>{' '}
                  <Text as='span' size='medium'>
                    transfers call
                  </Text>
                </Trans>
              ),
              time: endedAtTime,
            },
          ]
        : []),
    ];

    return (
      <>
        <EventRowList events={events} />

        {/* TODO: This reason probably needs to be accounted for - how do we decide which event's reason gets placed here */}
        {!callEndedByAi && (
          <Text color={reasonColor} textAlign='left' size='small' css={{ paddingTop: theme.spacing(2) }}>
            {reason}
          </Text>
        )}
      </>
    );
  }
);

AIReceptionistEventComponent.displayName = 'AIReceptionistEventComponent';
